/**
 * @unscoped — Cross-domain shared components 의 i18n — Cycle 71.
 *
 * cycle 70 의 component-level cleanup 시작 (NavBar/TopBar 25) 후속 — cross-domain reusable
 * components 의 hardcoded korean 봉합. 본 모듈이 cycle 71+ 의 component-level i18n SSOT.
 *
 * 적용 범위 (cycle 71):
 *   - StatusBadge (11 hardcoded) — approval status pill, 11 enum × ko 라벨
 *   - AttachmentList (8) — 파일 첨부 list (image / file 분기)
 *   - AttachmentUploader (6+) — 파일 첨부 picker + chip
 *   - Stepper (7) — 워크플로우 단계 progress (Approval + OnboardingWizard 등)
 *
 * 누락 (cycle 72+ 후보):
 *   - SidebarGroup 5 / AuthGuard 4 / Combobox 3 / AdminBanner 2 /
 *     SystemConnectingBanner 1 / ErrorBoundary 1 / Button 1
 *
 * cycle 51 calendar / cycle 54 mail / cycle 55 onboarding / cycle 57 board / cycle 58 task /
 * cycle 59-60 settings / cycle 61 chat / cycle 68 payroll / cycle 69 sysadmin / cycle 70 layout /
 * cycle 71 shared = 10 신규 모듈.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== StatusBadge — Approval 도메인 11 enum (frontend 6 + backend 5) =====
  // 시안 enum
  'shared.status.approved': { ko: '결재완료', en: 'Approved', ja: '承認完了' },
  'shared.status.in_progress': { ko: '결재중', en: 'In progress', ja: '審査中' },
  'shared.status.rejected': { ko: '반려', en: 'Rejected', ja: '差戻し' },
  'shared.status.pending_me': { ko: '승인대기', en: 'Pending', ja: '承認待ち' },
  'shared.status.approved_me': { ko: '승인완료', en: 'My approval', ja: '承認済み' },
  'shared.status.shared': { ko: '공유', en: 'Shared', ja: '共有' },
  // backend enum
  'shared.status.draft': { ko: '임시', en: 'Draft', ja: '一時保存' },
  'shared.status.recalled': { ko: '회수', en: 'Recalled', ja: '取消' },
  // (backend IN_PROGRESS/APPROVED/REJECTED 는 위 frontend 와 같은 키 매핑 — SSOT)

  // ===== AttachmentList =====
  // ===== WorkspaceFilePicker — 워크스페이스에서 첨부 (2026-08-23) =====
  // 어제 만들며 한글을 코드에 박아 뒀다. 도메인 중립 공용 컴포넌트라 여기(shared)에 둔다.
  'shared.wfp.title': { ko: '워크스페이스에서 첨부', en: 'Attach from Workspace', ja: 'ワークスペースから添付' },
  'shared.wfp.close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'shared.wfp.scope_recent': { ko: '최근 수정', en: 'Recent', ja: '最近の更新' },
  'shared.wfp.scope_mine': { ko: '내 파일', en: 'My files', ja: 'マイファイル' },
  'shared.wfp.scope_shared': { ko: '나에게 공유됨', en: 'Shared with me', ja: '共有された' },
  'shared.wfp.scope_company': { ko: '회사 공유', en: 'Company', ja: '全社共有' },
  'shared.wfp.kind_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'shared.wfp.kind_doc': { ko: '문서', en: 'Docs', ja: '文書' },
  'shared.wfp.kind_sheet': { ko: '표', en: 'Sheets', ja: '表' },
  'shared.wfp.kind_pdf': { ko: 'PDF', en: 'PDF', ja: 'PDF' },
  'shared.wfp.kind_image': { ko: '이미지', en: 'Images', ja: '画像' },
  'shared.wfp.search_ph': { ko: '파일 이름·문서 제목으로 찾기', en: 'Search by file or document title', ja: 'ファイル名・文書タイトルで検索' },
  'shared.wfp.search_aria': { ko: '파일 검색', en: 'Search files', ja: 'ファイル検索' },
  'shared.wfp.list_aria': { ko: '파일 목록', en: 'File list', ja: 'ファイル一覧' },
  'shared.wfp.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'shared.wfp.empty': { ko: '파일이 없습니다.', en: 'No files.', ja: 'ファイルがありません。' },
  'shared.wfp.load_failed': { ko: '목록을 불러오지 못했습니다', en: "Couldn't load the list", ja: '一覧を読み込めませんでした' },
  // 왜 못 고르나 — 서버가 막는 것을 화면이 먼저 말해 준다.
  'shared.wfp.why_already': { ko: '이미 첨부함', en: 'Already attached', ja: '添付済み' },
  'shared.wfp.why_uploading': { ko: '아직 올리는 중', en: 'Still uploading', ja: 'アップロード中' },
  'shared.wfp.why_not_mine': {
    ko: '내가 올린 파일이 아니라 바로 못 붙인다',
    en: "Not uploaded by you — can't attach directly",
    ja: '自分がアップロードしたものではないため添付できません',
  },
  'shared.wfp.selected': { ko: '{n}개 선택', en: '{n} selected', ja: '{n}件 選択' },
  'shared.wfp.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'shared.wfp.attach': { ko: '첨부', en: 'Attach', ja: '添付' },
  'shared.attach.list_aria': { ko: '첨부 파일', en: 'Attachments', ja: '添付ファイル' },
  'shared.attach.image_loading': { ko: '이미지 로드 중', en: 'Loading image', ja: '画像読み込み中' },
  'shared.attach.image_fallback': { ko: '이미지', en: 'Image', ja: '画像' },
  'shared.attach.image_alt': { ko: '이미지 첨부', en: 'Image attachment', ja: '画像添付' },
  'shared.attach.file_fallback': { ko: '첨부 파일', en: 'Attachment', ja: '添付ファイル' },
  'shared.attach.download_aria': {
    ko: '{name} 다운로드',
    en: 'Download {name}',
    ja: '{name} をダウンロード',
  },
  'shared.attach.file_unknown_name': { ko: '파일', en: 'File', ja: 'ファイル' },
  // chat-file-preview (2026-07-02) — 파일 카드 클릭 시 미리보기(가능 형식) 또는 미지원 경고 모달.
  'shared.attach.preview_open_aria': {
    ko: '{name} 미리보기',
    en: 'Preview {name}',
    ja: '{name} をプレビュー',
  },
  'shared.attach.preview_unavailable_title': {
    ko: '미리보기 불가',
    en: 'Preview unavailable',
    ja: 'プレビュー不可',
  },
  'shared.attach.preview_unavailable': {
    ko: '미리보기를 지원하지 않는 파일 형식입니다.',
    en: 'Preview is not supported for this file type.',
    ja: 'このファイル形式はプレビューに対応していません。',
  },
  'shared.attach.confirm': { ko: '확인', en: 'OK', ja: 'OK' },
  'shared.attach.close': { ko: '닫기', en: 'Close', ja: '閉じる' },

  // ===== AttachmentUploader =====
  'shared.upload.list_aria': { ko: '첨부 파일', en: 'Attachments', ja: '添付ファイル' },
  'shared.upload.remove_aria': {
    ko: '첨부 {name} 제거',
    en: 'Remove attachment {name}',
    ja: '添付 {name} を削除',
  },
  'shared.upload.uploading_percent': {
    ko: '업로드 중 {pct}%',
    en: 'Uploading {pct}%',
    ja: 'アップロード中 {pct}%',
  },
  'shared.upload.at_limit_aria': {
    ko: '첨부 최대 {max}개 도달',
    en: 'Reached max {max} attachments',
    ja: '添付最大 {max} 個に到達',
  },
  'shared.upload.attach_aria': { ko: '파일 첨부', en: 'Attach file', ja: 'ファイル添付' },
  'shared.upload.at_limit_title': {
    ko: '첨부는 최대 {max}개까지 가능합니다',
    en: 'Up to {max} attachments allowed',
    ja: '添付は最大 {max} 個まで可能です',
  },

  // ===== Stepper =====
  'shared.step.status.pending': { ko: '대기', en: 'Pending', ja: '待機' },
  'shared.step.status.active': { ko: '진행 중', en: 'In progress', ja: '進行中' },
  'shared.step.status.done': { ko: '완료', en: 'Done', ja: '完了' },
  'shared.step.status.rejected': { ko: '반려', en: 'Rejected', ja: '差戻し' },
  'shared.step.status.skipped': { ko: '건너뜀', en: 'Skipped', ja: 'スキップ' },
  'shared.step.aria_step': {
    ko: '{n}단계: {label} — {status}',
    en: 'Step {n}: {label} — {status}',
    ja: 'ステップ {n}: {label} — {status}',
  },

  // ===== Cycle 72 — AdminBanner / SystemConnectingBanner / SidebarGroup / Combobox / ErrorBoundary =====

  // ----- AdminBanner (24h grace 안내) -----
  'shared.admin_banner.title': { ko: '가입 안내 (24h grace)', en: 'Sign-up notice (24h grace)', ja: '登録案内 (24h grace)' },
  'shared.admin_banner.body': {
    ko: '회사 가입 후 24시간 동안 일부 관리 작업이 제한됩니다 ({remaining}). 본인 도메인이 맞는지 확인해 주세요.',
    en: 'For 24 hours after sign-up, some admin actions are restricted ({remaining}). Please confirm this is your domain.',
    ja: '会社登録後24時間は一部の管理操作が制限されます ({remaining})。自分のドメインか確認してください。',
  },
  'shared.admin_banner.remaining_h_m': {
    ko: '{h}시간 {m}분 남음',
    en: '{h}h {m}m left',
    ja: '残り {h}時間 {m}分',
  },
  'shared.admin_banner.remaining_m': {
    ko: '{m}분 남음',
    en: '{m}m left',
    ja: '残り {m}分',
  },
  'shared.admin_banner.contact_aria': {
    ko: '도메인 소유 관련 이의 제기 — 운영팀 contact (새 창 열림)',
    en: 'Domain ownership dispute — contact operations (opens new window)',
    ja: 'ドメイン所有に関する異議申し立て — 運営チーム連絡 (新規ウィンドウ)',
  },
  'shared.admin_banner.contact_label': {
    ko: '이의 제기 →',
    en: 'File dispute →',
    ja: '異議申し立て →',
  },

  // ----- SystemConnectingBanner (외부 시스템 연동 대기) -----
  'shared.connecting_banner.suffix_text': {
    ko: ' 연결 중',
    en: ' connecting',
    ja: ' 接続中',
  },
  'shared.connecting_banner.preview_note': {
    ko: ' — 임시 데이터로 미리보기 중입니다. 실데이터는 시스템 연동 후 표시됩니다.',
    en: ' — previewing with temporary data. Real data will appear after system integration.',
    ja: ' — 仮データでプレビュー中。実データはシステム連携後に表示されます。',
  },

  // ----- SidebarGroup (sub-item disabled tooltip / pending pill) -----
  'shared.sidebar_group.pending': { ko: '준비 중', en: 'Coming soon', ja: '準備中' },

  // ----- Combobox (default placeholder / search / empty) -----
  'shared.combobox.default_placeholder': { ko: '선택', en: 'Select', ja: '選択' },
  'shared.combobox.search_placeholder': { ko: '검색', en: 'Search', ja: '検索' },
  'shared.combobox.empty': { ko: '결과 없음', en: 'No results', ja: '結果なし' },

  // ----- ErrorBoundary -----
  'shared.error_boundary.heading': {
    ko: '오류가 발생했습니다',
    en: 'An error occurred',
    ja: 'エラーが発生しました',
  },
  'shared.error_boundary.btn_retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },

  // ----- SignatureHost (2026-09-11) — 서명 묶음을 받아 오는 동안 -----
  'shared.signature.loading': {
    ko: '서명 창을 불러오는 중…',
    en: 'Opening signature…',
    ja: '署名を読み込み中…',
  },

  // ----- CommentThread (정본 E10 · 2026-09-15) — 게시판 댓글부터 쓴다 -----
  'shared.comment.list_aria': { ko: '댓글', en: 'Comments', ja: 'コメント' },
  'shared.comment.reply': { ko: '답글', en: 'Reply', ja: '返信' },
  'shared.comment.edit': { ko: '수정', en: 'Edit', ja: '編集' },
  'shared.comment.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'shared.comment.deleted': { ko: '삭제된 댓글입니다', en: 'This comment was deleted', ja: '削除されたコメントです' },
  'shared.comment.placeholder': { ko: '의견을 남기세요', en: 'Leave a comment', ja: 'コメントを入力' },
  'shared.comment.input_aria': { ko: '댓글 입력', en: 'Comment', ja: 'コメント入力' },
  'shared.comment.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'shared.comment.submit': { ko: '등록', en: 'Post', ja: '登録' },
  'shared.comment.just_now': { ko: '방금', en: 'Just now', ja: 'たった今' },
  'shared.comment.minutes_ago': { ko: '{n}분 전', en: '{n} min ago', ja: '{n}分前' },
  'shared.comment.hours_ago': { ko: '{n}시간 전', en: '{n} hr ago', ja: '{n}時間前' },
};

export type SharedI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 71 — Cross-domain shared components i18n lookup.
 * params 치환 — cycle 51-70 의 10 도메인 모듈과 일관.
 */
export function tShared(key: SharedI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getSharedComponentLang(): Lang { return getSharedLang(); }
export function setSharedComponentLang(value: Lang): void { setSharedLang(value); }
export function subscribeSharedComponentLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
